import React, { useState, useEffect } from 'react';
import { LockerType, LockerTypeFormData } from '../types/locker';
import FormSlider from './FormSlider';
import api from '../services/api';
import '../styles/DrawerForm.css';
import '../styles/Buttons.css';

interface LockerTypeFormSliderProps {
  workspaceId: string;
  typeId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

interface Project {
  _id: string;
  name: string;
}

const LOCKER_IMAGES = [
  { color: '#000000', name: 'Black', standardImage: '/locker-icons/black-single-locker.png', selectedImage: '/locker-icons/black-single-locker.png' },
  { color: '#0000FF', name: 'Blue', standardImage: '/locker-icons/blue-single-locker.png', selectedImage: '/locker-icons/blue-single-locker.png' },
  { color: '#00BFFF', name: 'Light Blue', standardImage: '/locker-icons/light-blue-single-locker.png', selectedImage: '/locker-icons/light-blue-single-locker.png' },
  { color: '#00FFFF', name: 'Mint', standardImage: '/locker-icons/mint-single-locker.png', selectedImage: '/locker-icons/mint-single-locker.png' },
  { color: '#00FF00', name: 'Green', standardImage: '/locker-icons/green-single-locker.png', selectedImage: '/locker-icons/green-single-locker.png' },
  { color: '#FFA500', name: 'Orange', standardImage: '/locker-icons/orange-single-locker.png', selectedImage: '/locker-icons/orange-single-locker.png' },
  { color: '#FF00FF', name: 'Pink', standardImage: '/locker-icons/pink-single-locker.png', selectedImage: '/locker-icons/pink-single-locker.png' },
  { color: '#800080', name: 'Purple', standardImage: '/locker-icons/purple-single-locker.png', selectedImage: '/locker-icons/purple-single-locker.png' },
  { color: '#FF0000', name: 'Red', standardImage: '/locker-icons/red-single-locker.png', selectedImage: '/locker-icons/red-single-locker.png' },
  { color: '#FFD700', name: 'Yellow', standardImage: '/locker-icons/yellow-single-locker.png', selectedImage: '/locker-icons/yellow-single-locker.png' },
];

const LockerTypeFormSlider: React.FC<LockerTypeFormSliderProps> = ({
  workspaceId,
  typeId,
  onSuccess,
  onCancel
}) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [initialProjectId, setInitialProjectId] = useState('');
  const [standardImage, setStandardImage] = useState('/locker-icons/light-blue-single-locker.png');
  const [selectedImage, setSelectedImage] = useState('/locker-icons/red-single-locker.png');

  const initialFormData: LockerTypeFormData = {
    projectId: initialProjectId,
    name: '',
    total: '0',
    available: '0',
    marketingHold: '0',
    salesHold: '0',
    sold: '0',
    customerSelection: '0',
    customerSubmitted: '0'
  };

  const fetchProjects = async () => {
    try {
      setLoadingProjects(true);
      const response = await api.get(`/workspaces/${workspaceId}/projects`, {
        
        params: {
          limit: 1000,
          sortBy: 'name',
          sortOrder: 'asc'
        }
      });
      const projectsList = response.data.projects;
      setProjects(projectsList);

      // Auto-select first project if creating new type
      if (!typeId && projectsList.length > 0) {
        setInitialProjectId(projectsList[0]._id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [workspaceId]);

  const validateForm = (data: LockerTypeFormData): string | null => {
    if (!data.projectId) {
      return 'Project selection is required';
    }
    if (!data.name.trim()) {
      return 'Locker type name is required';
    }
    return null;
  };

  const transformFetchData = (response: any): LockerTypeFormData => {
    const type: LockerType = response.lockerType;

    // Set image states from fetched data
    if (type.standardImage) {
      setStandardImage(type.standardImage);
    }
    if (type.selectedImage) {
      setSelectedImage(type.selectedImage);
    }

    return {
      projectId: type.projectId || '',
      name: type.name || '',
      total: type.total?.toString() || '0',
      available: type.available?.toString() || '0',
      marketingHold: type.marketingHold?.toString() || '0',
      salesHold: type.salesHold?.toString() || '0',
      sold: type.sold?.toString() || '0',
      customerSelection: type.customerSelection?.toString() || '0',
      customerSubmitted: type.customerSubmitted?.toString() || '0'
    };
  };

  const transformSubmitData = (data: LockerTypeFormData) => {
    return {
      projectId: data.projectId,
      name: data.name.trim(),
      standardImage: standardImage,
      selectedImage: selectedImage,
      total: data.total ? parseInt(data.total) : 0,
      available: data.available ? parseInt(data.available) : 0,
      marketingHold: data.marketingHold ? parseInt(data.marketingHold) : 0,
      salesHold: data.salesHold ? parseInt(data.salesHold) : 0,
      sold: data.sold ? parseInt(data.sold) : 0,
      customerSelection: data.customerSelection ? parseInt(data.customerSelection) : 0,
      customerSubmitted: data.customerSubmitted ? parseInt(data.customerSubmitted) : 0
    };
  };

  if (!typeId && loadingProjects) {
    return (
      <div className="builders-loading" style={{ padding: '64px 0' }}>
        <div className="builders-loading-spinner" />
      </div>
    );
  }

  return (
    <FormSlider
      key={initialProjectId}
      workspaceId={workspaceId}
      itemId={typeId}
      onSuccess={onSuccess}
      onCancel={onCancel}
      apiEndpoint={`/workspaces/${workspaceId}/locker-types`}
      initialFormData={initialFormData}
      validateForm={validateForm}
      transformFetchData={transformFetchData}
      transformSubmitData={transformSubmitData}
      formId="locker-type-form"
    >
      {({ formData, handleInputChange, loading }) => (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {/* Project */}
            <div className="form-group">
              <label htmlFor="projectId" className="form-label">
                Project
              </label>
              {loadingProjects ? (
                <div className="builders-loading" style={{ padding: '20px 0', textAlign: 'center' }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <select
                  id="projectId"
                  name="projectId"
                  className="form-input"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                >
                  {projects.map((project) => (
                    <option key={project._id} value={project._id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Name */}
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
          </div>

          {/* Standard Image */}
          <div className="form-group">
            <label className="form-label">
              Standard image:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginTop: '10px' }}>
              {LOCKER_IMAGES.map((item) => (
                <button
                  key={item.color}
                  type="button"
                  onClick={() => setStandardImage(item.standardImage)}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F4F6',
                    border: standardImage === item.standardImage ? '3px solid #4CAF50' : '2px solid #D1D5DB',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px'
                  }}
                  disabled={loading}
                  title={item.name}
                >
                  <img
                    src={item.standardImage}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  {standardImage === item.standardImage && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Image */}
          <div className="form-group">
            <label className="form-label">
              Selected image:
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '15px', marginTop: '10px' }}>
              {LOCKER_IMAGES.map((item) => (
                <button
                  key={item.color}
                  type="button"
                  onClick={() => setSelectedImage(item.selectedImage)}
                  style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '8px',
                    backgroundColor: '#F3F4F6',
                    border: selectedImage === item.selectedImage ? '3px solid #4CAF50' : '2px solid #D1D5DB',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px'
                  }}
                  disabled={loading}
                  title={item.name}
                >
                  <img
                    src={item.selectedImage}
                    alt={item.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                  />
                  {selectedImage === item.selectedImage && (
                    <div style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: '#4CAF50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '14px',
                      fontWeight: 'bold'
                    }}>
                      ✓
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </FormSlider>
  );
};

export default LockerTypeFormSlider;
